import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Cancel, FileCopy } from '@material-ui/icons';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { useQuery } from 'hooks/useQuery';
import { FormikTextFieldComponent } from 'components/Field';
import { Confluence } from '../interfaces';
import { useCloneArticle } from '../hooks';
import { cloneName, flatMenu, parseSection, SECTION_LABELS, sectionSuffix } from '../helpers';
import { loadConfluenceMenu } from '../api';

interface IProps extends Omit<DialogProps, 'onClose'> {
  article: Confluence.IArticle | null;
  menu: Confluence.IMenu;
  onClose(): void;
  onClone?(cloneId: number, section: Confluence.ESections): void;
}

interface IFormValues {
  name: string;
  parent: Confluence.IArticle | null;
}

const CloneArticleDialog = ({ open, article, menu, onClose, onClone, ...props }: IProps) => {
  const params = useQuery();

  const currentSection = parseSection(params.get('section'));

  const [section, setSection] = useState<Confluence.ESections>(currentSection);
  const [sectionMenu, setSectionMenu] = useState<Confluence.IMenu>(menu || []);

  const { loading, handleCloneArticle } = useCloneArticle(article, (cloneId) => {
    onClose();
    // целевой раздел отдаём странице: она обновит URL, дерево и откроет копию одним проходом
    if (onClone) onClone(cloneId, section);
  });

  useEffect(() => {
    if (!open) return;

    setSection(currentSection);
  }, [open, currentSection]);

  useEffect(() => {
    if (!open) return;

    if (section === currentSection) {
      setSectionMenu(menu || []);
      return;
    }

    loadConfluenceMenu({ params: { section } }).then(setSectionMenu);
  }, [open, section, currentSection, menu]);

  const initialValues: IFormValues = {
    // раздел берём из URL, а не из состояния: на первом рендере после открытия оно ещё не синхронизировано
    name: cloneName(article?.name, currentSection),
    parent: null,
  };

  const validationSchema = yup.object().shape({
    name: yup.string().trim().max(2048, 'Не больше 2048 символов').required('Укажите наименование копии'),
    parent: yup.object().nullable(),
  });

  return (
    <Dialog {...props} open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle>
        <b>Клонировать статью</b>
      </DialogTitle>

      <Formik
        onSubmit={(values) => handleCloneArticle(values.name.trim(), values.parent?.id, section)}
        validationSchema={validationSchema}
        initialValues={initialValues}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    name="name"
                    variant="filled"
                    fullWidth
                    label="Наименование копии"
                    disabled={loading}
                    component={FormikTextFieldComponent}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    value={section}
                    variant="filled"
                    label="Раздел"
                    fullWidth
                    disabled={loading}
                    onChange={(event) => {
                      const nextSection = event.target.value as Confluence.ESections;

                      // имя по умолчанию несёт раздел, поэтому обновляем его вместе с выбором,
                      // но не затираем наименование, которое пользователь уже поправил руками
                      if (values.name === cloneName(article?.name, section)) {
                        setFieldValue('name', cloneName(article?.name, nextSection));
                      }

                      setSection(nextSection);
                      setFieldValue('parent', null);
                    }}
                  >
                    {Array.from(SECTION_LABELS).map(([value, label]) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    value={values.parent}
                    options={flatMenu(sectionMenu || [])}
                    onChange={(event, option) => setFieldValue('parent', option)}
                    disabled={loading}
                    getOptionLabel={(option) => String(option.name)}
                    getOptionSelected={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="parent"
                        variant="filled"
                        label="Родительская статья"
                        placeholder="Выберите из списка"
                        fullWidth
                        helperText={`Если вы не выберете родительскую статью, копия будет создана в корне раздела «${SECTION_LABELS.get(
                          section,
                        )}». Вложенные статьи клонируются вместе с ней: занятые наименования получат суффикс «${sectionSuffix(
                          section,
                        )}».`}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant="contained" disabled={loading} onClick={onClose} startIcon={<Cancel />}>
                Отменить
              </Button>

              <Button color="primary" variant="contained" disabled={loading} type="submit" startIcon={<FileCopy />}>
                Клонировать
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CloneArticleDialog;
